/*
  Meem Arabic Learning — Bundle Builder Sale Price Display
  24 October 2026

  Adds sale-price context to the existing bundle builders on:
  - Meem homepage
  - Arabic Alphabet, Reading & Vocabulary, Arabic Essentials, Fun Activities product pages

  Only uses Shopify's current variant price / compare-at price from the
  locale-aware Ajax Product API. Does not change prices, quantities, discounts,
  selections, cart handlers, thumbnails, or the original product templates.
*/
(() => {
  'use strict';
  if (window.__meemBundleDiscountDisplay) return;
  window.__meemBundleDiscountDisplay = true;

  const HANDLES = [
    'arabic-alphabet-bundle',
    'reading-vocabulary-bundle',
    'arabic-essentials-bundle',
    'fun-activities-bundle'
  ];
  const variants = new Map();
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const route = root.endsWith('/') ? root : `${root}/`;
  let loaded = false;
  let pending = false;

  // This respects the visitor's Shopify currency without showing a code such as "AUD".
  // A narrow symbol displays $15.00 for AUD and USD, £15.00 for GBP, etc.
  const currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'AUD';
  const locale = document.documentElement.lang || 'en-AU';
  let formatter;
  try {
    formatter = new Intl.NumberFormat(locale, {
      style: 'currency', currency, currencyDisplay: 'narrowSymbol'
    });
  } catch (_) {
    formatter = new Intl.NumberFormat('en-AU', {
      style: 'currency', currency: 'AUD', currencyDisplay: 'narrowSymbol'
    });
  }
  const money = cents => formatter.format(cents / 100);

  const css = `
    .mh-bundle-price.mbdp-prices,
    .ma-bundle-row .mbdp-prices {
      display: flex !important;
      flex-direction: column !important;
      align-items: flex-end;
      gap: 3px;
      text-align: right;
      flex-shrink: 0;
      line-height: 1.3;
    }
    .mh-bundle-price.mbdp-prices { margin-left: auto; }
    .mbdp-prices > b, .mbdp-prices > strong { display: block; font-weight: 750; }
    .mbdp-prices .mbdp-original {
      display: block; color: #817887; font-size: 11px;
      line-height: 1.4; text-decoration: line-through; white-space: nowrap;
    }
    .mbdp-prices .mbdp-percent {
      display: inline-block; align-self: flex-end;
      background: #f9dc78; color: #332446;
      border-radius: 5px; padding: 3px 7px;
      font-size: 10px; font-weight: 800; line-height: 1.45;
      white-space: nowrap; letter-spacing: .01em;
    }
    .mh-bundle-row .mbdp-prices > b { font-size: 14px; }
    .ma-bundle-row .mbdp-prices > strong { font-size: 14px; }
    .ma-bundle-row .mbdp-prices.mbdp-base {
      margin-left: auto;
    }
    .ma-bundle-row .mbdp-prices.mbdp-base > strong[data-included-price] {
      margin-left: 0 !important;
      font-size: 14px;
      white-space: nowrap;
    }
    .ma-bundle-row .ma-extra-price .mbdp-prices { width: max-content; max-width: 100%; }
    .mbdp-summary {
      display: flex; flex-wrap: wrap; justify-content: space-between;
      gap: 5px 12px; margin: 0 0 14px; font-size: 12px;
      line-height: 1.55;
    }
    .mbdp-summary s { color: #d1c3e5; }
    .mbdp-summary b { color: #f9dc78; font-weight: 750; }
    .mbdp-summary[hidden], .mbdp-original[hidden], .mbdp-percent[hidden] {
      display: none !important;
    }
    @media (max-width: 749px) {
      .mh-bundle-row .mbdp-prices > b,
      .ma-bundle-row .mbdp-prices > strong { font-size: 12px; }
      .ma-bundle-row .mbdp-prices.mbdp-base > strong[data-included-price] { font-size: 12px; }
      .mbdp-prices .mbdp-original { font-size: 10px; }
      .mbdp-prices .mbdp-percent { font-size: 9px; padding: 3px 5px; }
      .ma-bundle-row .mbdp-prices.mbdp-base {
        flex: 0 0 100%; width: 100%; margin-left: 0;
        padding-left: 35px; align-items: flex-start; text-align: left;
      }
      .ma-bundle-row .mbdp-prices.mbdp-base .mbdp-percent { align-self: flex-start; }
    }
  `;

  function addStyles() {
    if (document.getElementById('meem-bundle-discount-style')) return;
    const style = document.createElement('style');
    style.id = 'meem-bundle-discount-style';
    style.textContent = css;
    document.head.appendChild(style);
  }

  function selectedVariantId(row) {
    const input = row.querySelector('[data-bundle], [data-extra]');
    if (input) return String(input.dataset.variant || input.dataset.id || '');
    // The included row on each product page uses the product selector above it.
    const page = row.closest('meem-alphabet, meem-reading-vocab, meem-essentials, meem-fun');
    return String(page?.querySelector('[data-variant]')?.value || '');
  }

  function ensurePriceContainer(row) {
    if (row.classList.contains('mh-bundle-row')) {
      const price = row.querySelector('.mh-bundle-price');
      if (!price || !price.querySelector('b')) return null;
      price.classList.add('mbdp-prices');
      return price;
    }
    if (row.classList.contains('ma-included')) {
      let group = row.querySelector('.mbdp-prices.mbdp-base');
      if (group) return group;
      const price = row.querySelector('[data-included-price]');
      if (!price) return null;
      group = document.createElement('span');
      group.className = 'mbdp-prices mbdp-base';
      price.replaceWith(group);
      group.appendChild(price); // Keep data-included-price for existing variant updater.
      return group;
    }
    const box = row.querySelector('.ma-extra-price');
    if (!box) return null;
    let group = box.querySelector('.mbdp-prices');
    if (group) return group;
    const price = box.querySelector('strong');
    if (!price) return null;
    group = document.createElement('span');
    group.className = 'mbdp-prices';
    price.replaceWith(group);
    group.appendChild(price); // Do not touch the existing thumbnail / preview control.
    return group;
  }

  function ensureLabel(container, className, tagName) {
    let label = container.querySelector(`.${className}`);
    if (!label) {
      label = document.createElement(tagName);
      label.className = className;
      container.appendChild(label);
    }
    return label;
  }

  function renderRow(row) {
    const current = variants.get(selectedVariantId(row));
    if (!current) return;
    const price = Number(current.price);
    const before = Number(current.compare_at_price);
    const container = ensurePriceContainer(row);
    if (!container) return;
    const original = ensureLabel(container, 'mbdp-original', 's');
    const discount = ensureLabel(container, 'mbdp-percent', 'span');
    if (Number.isFinite(price) && Number.isFinite(before) && before > price && before > 0) {
      const percent = Math.round((before - price) * 100 / before);
      original.textContent = money(before);
      discount.textContent = `${percent}% OFF`;
      original.hidden = discount.hidden = false;
    } else {
      original.hidden = discount.hidden = true;
    }
  }

  function renderSummary(builder) {
    const totalLine = builder.querySelector('.mh-order-total, .ma-order-total');
    if (!totalLine) return;
    const selectedRows = [...builder.querySelectorAll('.mh-bundle-row, .ma-bundle-row')]
      .filter(row => {
        const input = row.querySelector('input[type="checkbox"]');
        return input && input.checked && !input.disabled;
      });

    let note = builder.querySelector('.mbdp-summary');
    if (!note) {
      note = document.createElement('div');
      note.className = 'mbdp-summary';
      const original = document.createElement('s');
      const save = document.createElement('b');
      note.append(original, save);
      totalLine.insertAdjacentElement('afterend', note);
    }

    let actual = 0;
    let compared = 0;
    for (const row of selectedRows) {
      const v = variants.get(selectedVariantId(row));
      if (!v || !Number.isFinite(Number(v.price))) {
        note.hidden = true;
        return; // Never display an incomplete/misleading sum.
      }
      const price = Number(v.price);
      const compare = Number(v.compare_at_price);
      actual += price;
      compared += Number.isFinite(compare) && compare > price ? compare : price;
    }
    if (!selectedRows.length || compared <= actual) {
      note.hidden = true;
      return;
    }
    note.querySelector('s').textContent = `Regular total ${money(compared)}`;
    note.querySelector('b').textContent = `You save ${money(compared - actual)}`;
    note.hidden = false;
  }

  function render(scope = document) {
    if (!loaded) return;
    addStyles();
    scope.querySelectorAll?.('.mh-bundle-row, .ma-bundle-row').forEach(renderRow);
    scope.querySelectorAll?.('meem-homepage, meem-alphabet, meem-reading-vocab, meem-essentials, meem-fun')
      .forEach(builder => renderSummary(builder));
    if (scope.matches?.('meem-homepage, meem-alphabet, meem-reading-vocab, meem-essentials, meem-fun')) {
      renderSummary(scope);
    }
  }

  function queueRender() {
    if (pending) return;
    pending = true;
    requestAnimationFrame(() => { pending = false; render(); });
  }

  async function load() {
    addStyles();
    const products = await Promise.allSettled(HANDLES.map(async handle => {
      const response = await fetch(`${route}products/${handle}.js`, {credentials: 'same-origin'});
      if (!response.ok) throw new Error(`${handle}: ${response.status}`);
      return response.json();
    }));
    products.forEach((result, index) => {
      if (result.status !== 'fulfilled') {
        console.warn('[Meem bundle sale prices] Could not load', HANDLES[index]);
        return;
      }
      for (const variant of (result.value.variants || [])) {
        variants.set(String(variant.id), variant);
      }
    });
    loaded = true;
    render();
  }

  // The existing homepage preset buttons change checkboxes programmatically.
  // Listen for clicks AND change events to refresh the savings summary.
  document.addEventListener('change', event => {
    if (event.target.matches('[data-bundle], [data-base], [data-extra], [data-variant]')) {
      queueRender();
    }
  });
  document.addEventListener('click', event => {
    if (event.target.closest('[data-preset], [data-select-all], [data-clear]')) {
      queueRender();
    }
  });
  document.addEventListener('shopify:section:load', event => {
    if (!loaded) return;
    render(event.target);
  });
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', load, {once: true});
  } else {
    load();
  }
})();
